/**
 * "N permissions customized" on the users table -- visible only to
 * SUPER_ADMIN/ADMIN (gated by the caller via canCustomizeUserPermissions,
 * same flag as the modal's customize action). Renders nothing for a user
 * with no overrides: `overrideCount` comes straight off the users list
 * response (avrio-backend#110), so no extra request is needed just to
 * decide whether to show the badge at all.
 *
 * Clicking it fetches GET /api/users/{userId}/permissions -- a real,
 * per-click call, not per-row on page load -- and shows exactly which
 * permissions are overridden and to what state, not just that something
 * is customized. Labels come from CAPABILITY_GROUPS, the same source the
 * role cards and the customize-permissions preview already read, so this
 * can't describe a permission differently than they do.
 */

import { memo, useState } from 'react';
import { Popover, Tag, Spin, Typography } from 'antd';
import axios from '../../../../axiosConfig';
import { CAPABILITY_GROUPS } from '../../utils/roleCapabilities';
import { Permission } from '../../utils/role';
import styles from './index.module.css';

const { Text } = Typography;

interface PermissionOverride {
  permission: string;
  granted: boolean;
}

interface UserPermissionsResponse {
  overrides: PermissionOverride[];
}

const permissionLabel = (permission: string): string =>
  CAPABILITY_GROUPS.find((item) =>
    item.permissions.includes(permission as Permission),
  )?.label ?? permission;

export interface PermissionOverrideBadgeProps {
  userId: number;
  overrideCount?: number;
}

export const PermissionOverrideBadge = memo(
  ({ userId, overrideCount }: PermissionOverrideBadgeProps) => {
    const [overrides, setOverrides] = useState<PermissionOverride[] | null>(
      null,
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!overrideCount) return null;

    const label = `${overrideCount} ${overrideCount === 1 ? 'permission' : 'permissions'} customized`;

    const handleOpenChange = (open: boolean) => {
      if (!open || overrides !== null || loading) return;
      setLoading(true);
      setError(null);
      axios
        .get<UserPermissionsResponse>(`/api/users/${userId}/permissions`)
        .then((res) => setOverrides(res.data.overrides))
        .catch(() => setError('Could not load what changed.'))
        .finally(() => setLoading(false));
    };

    const content = (
      <div className={styles.content}>
        {loading && <Spin size="small" />}
        {error && <Text type="danger">{error}</Text>}
        {overrides && (
          <ul className={styles.list}>
            {overrides.map((o) => (
              <li key={o.permission}>
                {permissionLabel(o.permission)}:{' '}
                <strong>{o.granted ? 'granted' : 'revoked'}</strong>
              </li>
            ))}
          </ul>
        )}
      </div>
    );

    return (
      <Popover
        content={content}
        trigger="click"
        onOpenChange={handleOpenChange}
      >
        <Tag
          className={styles.tag}
          role="button"
          tabIndex={0}
          aria-label={`${label} — click to see what changed`}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.currentTarget.click();
            }
          }}
        >
          {label}
        </Tag>
      </Popover>
    );
  },
);

PermissionOverrideBadge.displayName = 'PermissionOverrideBadge';

export default PermissionOverrideBadge;
